import { Flame, Sprout, HeartHandshake, Trees, Baby } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "@/lib/lenis";

const HIGHLIGHTS = [
  {
    no: "01",
    icon: Flame,
    title: "Gobar Gas plant",
    gujarati: "ગોબર ગેસ પ્લાન્ટ",
    body: "Cow dung from the shed feeds our own biogas plant — clean cooking fuel for the farm, and nutrient-rich slurry that goes back to the fields as natural manure. Nothing is wasted.",
  },
  {
    no: "02",
    icon: Sprout,
    title: "Natural grass farming & 100% natural feed",
    gujarati: "કુદરતી ઘાસચારો",
    body: "We grow our own green fodder without chemical fertilisers or pesticides. Every cow eats 100% natural food — fresh grass, dry fodder and farm-grown grain. No commercial feed, no additives.",
  },
  {
    no: "03",
    icon: Baby,
    title: "Genuine in-house reproduction",
    gujarati: "કુદરતી પ્રજનન",
    body: "Every calf on the farm is born here through natural, genuine reproduction of our own herd — no hormone injections, no bought-in stock. Lineage you can trace, health you can trust.",
  },
  {
    no: "04",
    icon: Trees,
    title: "Big open farm for grazing",
    gujarati: "ખુલ્લું ચરિયાણ",
    body: "Our cows are not tied in stalls all day. A large open farm lets the herd roam and graze freely — sunlight, exercise and fresh grass make for calmer cows and better milk.",
  },
  {
    no: "05",
    icon: HeartHandshake,
    title: "Caring for non-lactating cows",
    gujarati: "દરેક ગાયની સંભાળ",
    body: "At Adveshta Projects no cow is abandoned when she stops giving milk. Non-lactating cows stay with the herd, cared for and engaged in normal farm work — for life.",
  },
];

const PHOTOS = [
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_village-dairy-co/artifacts/cvl1xbpr_gobar-gas-plant-1000x1000.webp",
    alt: "Adveshta gobar gas (biogas) plant beside the cattle shed at Khushalpura",
    caption: "Our gobar gas plant · Khushalpura farm",
  },
  {
    src: "https://images.unsplash.com/photo-1739066112286-be4f0662ec9f?auto=format&fit=crop&w=900&q=80",
    alt: "Cow with her calf at the farm",
    caption: "Calf born in-house",
  },
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_village-dairy-co/artifacts/dp8bfchv_grazing%20land%20COW.jfif",
    alt: "Adveshta Gir cows grazing on the open farm land",
    caption: "Our grazing land",
  },
];

export default function Farm() {
  return (
    <section id="farm" data-testid="farm-section" className="bg-pine text-cream py-24 lg:py-36 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-10 -right-6 font-serif font-semibold text-[22vw] leading-none text-outline-cream"
      >
        ગાય
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow>05 — The Farm</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight">
              A farm that runs
              <br />
              <span className="italic text-gold">the way nature intended.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-cream/70 leading-relaxed max-w-md lg:ml-auto">
              Five things we do differently at Khushalpura — from the gas we cook with to the way
              every cow is cared for long after she stops giving milk. Come and see it for yourself.
            </p>
            <button
              data-testid="farm-book-visit-button"
              onClick={() => scrollToSection("visit")}
              className="mt-6 inline-flex items-center gap-3 rounded-full bg-gold text-ink px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-cream transition-colors duration-300"
            >
              Book a farm visit
            </button>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 grid gap-5">
            <Reveal>
              <figure className="relative overflow-hidden rounded-3xl h-[300px] lg:h-[460px] group">
                <img
                  src={PHOTOS[0].src}
                  alt={PHOTOS[0].alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-ink/70 backdrop-blur-sm px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/85">
                  {PHOTOS[0].caption}
                </figcaption>
              </figure>
            </Reveal>
            <div className="grid grid-cols-2 gap-5">
              {PHOTOS.slice(1).map((p, i) => (
                <Reveal key={p.src} delay={0.1 + i * 0.08}>
                  <figure className="relative overflow-hidden rounded-3xl h-[200px] lg:h-[240px] group">
                    <img
                      src={p.src}
                      alt={p.alt}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 bg-ink/70 backdrop-blur-sm px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.22em] text-cream/85">
                      {p.caption}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.no} delay={i * 0.07}>
                <div
                  data-testid={`farm-highlight-${h.no}`}
                  className="group border-t border-cream/15 py-8 md:py-9 px-2 md:px-6 transition-colors duration-500 hover:bg-cream/5 last:border-b"
                >
                  <div className="flex items-start gap-5 md:gap-8">
                    <span className="font-mono text-sm text-gold pt-2 shrink-0">{h.no}</span>
                    <span className="hidden sm:inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold group-hover:bg-gold group-hover:text-ink transition-colors duration-500">
                      <h.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-[1.75rem] leading-tight group-hover:text-gold transition-colors duration-500">
                        {h.title}
                      </h3>
                      <p className="mt-1 font-gujarati text-sm text-gold/80">{h.gujarati}</p>
                      <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-cream/65 max-w-xl">
                        {h.body}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
